import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { KompisText, KompisToggle } from '@inter-ikea-kompis/react-components';
import styles from './ProductMenuSubFilter.module.less';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import { metricToImperial } from '../../util/measures';
import { selectUseMetric } from '../../state/dexfSettings/dexfSettingsSelectors';
import {
  selectIsMobileOrTablet,
  selectIsPortrait,
  selectIsLandscape,
} from '../../state/userAgent/userAgentSelectors';
import {
  selectCurrentSubFilterIndex,
  selectProductMenuFilter,
} from '../../state/productMenu/productMenuSelectors';
import { SubfilterOption } from '../../state/productMenu/productMenuTypes';
import IToggleItem from '@inter-ikea-kompis/component-toggle/lib/types/IToggleItem';
import { actionSetSwiperSubFilter } from '../../state/productMenu/productMenuActions';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';

const ProductMenuSubFilter = () => {
  const dispatch = useDispatch();

  const setSwiperSubFilter = (subFilter: number) =>
    dispatch(actionSetSwiperSubFilter(subFilter));

  const currentFilter = useSelector(selectProductMenuFilter);
  const currentSubFilter = useSelector(selectCurrentSubFilterIndex);
  const shouldUseMetric = useSelector(selectUseMetric);
  const isMobileOrTablet = useSelector(selectIsMobileOrTablet);
  const isPortrait = useSelector(selectIsPortrait);
  const isLandscape = useSelector(selectIsLandscape);

  const { key, options, preferToDisableRatherThanHideEmpty } =
    currentFilter.subFilter;
  const optionsWithItems = options.filter(option => option.hasItems);

  useEffect(() => {
    if (isInDisabledMode() && !options[currentSubFilter]?.hasItems) {
      const optionToSelect = optionsWithItems[0];
      const indexOfOptionToSelect = options.indexOf(optionToSelect);
      setSwiperSubFilter(indexOfOptionToSelect);
    }
  }, [currentSubFilter]);

  /**
   * Checks criteria to decide whether currently in disabled mode.
   * @returns {boolean} Whether currently in disabled mode.
   */
  const isInDisabledMode = (): boolean =>
    /* Disabled mode is currently not supported for more than two options,
       since KompisToggle cannot handle scenarios where there are two or
       more enabled options while some option is disabled. */
    !!preferToDisableRatherThanHideEmpty &&
    options.length === 2 &&
    optionsWithItems.length === 1;

  /**
   * Get options to display
   * @returns {SubfilterOption[]} The options to display depending on mode.
   */
  const getOptionsToDisplay = (): SubfilterOption[] =>
    isInDisabledMode() ? options : optionsWithItems;

  /**
   * Get metric
   * @returns {number|string}
   */
  const getMetric = (value: number) =>
    shouldUseMetric ? value / 10 : metricToImperial(value).onlyInches;

  /**
   * Get unit
   * @param option
   * @returns {string|*}
   */
  const getUnit = (option: SubfilterOption) =>
    `${getMetric(getLabel(option))} ${
      shouldUseMetric
        ? translate(t.MEASURE_VALUE_CM)
        : translate(t.MEASURE_VALUE_IN)
    }`;

  /**
   * Allows for override label
   * @param overrideLabel
   * @param name
   */
  const getLabel = ({ overrideLabel, value }: SubfilterOption) =>
    overrideLabel ? overrideLabel : value;

  /**
   * Get label
   * @returns {*}
   */
  const getLabels = (): IToggleItem[] =>
    getOptionsToDisplay().map((option: SubfilterOption) => ({
      label: `${option.name && getUnit(option)}`,
      icon: '',
    }));

  /**
   * Set sub filter
   * @param selectedIndex
   * @private
   */
  const _setSubFilter = ({
    detail: { selectedIndex },
  }: CustomEvent<{ selectedIndex: number }>) => {
    setSwiperSubFilter(selectedIndex);
    const filter = currentFilter.name;
    const subFilter = key;
    const option = options[selectedIndex].value?.toString();
    option &&
      localStatisticsReporter.reportProductMenuSubFilterInteraction(
        filter,
        subFilter,
        option
      );
  };

  /**
   * Render header label
   * @returns {false|JSX.Element}
   */
  const renderHeaderLabel = () =>
    isLandscape && (
      <div className={styles.headerLabel}>
        <KompisText theme={SkapaTheme} bold={true}>
          {`${translate(t[currentFilter.subFilter.labelTranslationKey])}:`}
        </KompisText>
      </div>
    );

  return getOptionsToDisplay().length > 1 ? (
    <div className={styles.wrapper}>
      {renderHeaderLabel()}
      <KompisToggle
        items={getLabels()}
        selectedIndex={currentSubFilter}
        disabled={isInDisabledMode()}
        fluidWidth={!(isMobileOrTablet && isPortrait)}
        theme={SkapaTheme}
        onSelect={_setSubFilter}
      />
    </div>
  ) : null;
};

export default ProductMenuSubFilter;
